import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import { Mic, Crown, Handshake, Sparkles } from "lucide-react";
import PrivacyScoreBadge from "../components/PrivacyScoreBadge";

const SplashPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [confirmType, setConfirmType] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const handleConfirm = (type) => {
    navigate(`/auth?type=${type}`);
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/home", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    // Capture referral code from URL and persist for signup
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) localStorage.setItem("pending_referral", ref);
    return () => clearTimeout(timer);
  }, []);

  if (isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center overflow-x-hidden py-8 px-4" data-testid="splash-page">
      
      {/* Logo — centered, fits viewport */}
      <div className={`transition-all duration-1000 mb-8 ${loaded ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`} data-testid="splash-logo-frame">
        <img
          src="/logo.png"
          alt="StardomFans.com"
          className="max-h-[45vh] w-auto max-w-[85vw] object-contain"
          style={{ mixBlendMode: "lighten" }}
          data-testid="splash-logo"
        />
      </div>

      {/* Existing entry actions, aligned to the logo tagline. */}
      <div className={`grid grid-cols-2 transition-all duration-1000 delay-500 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} data-testid="splash-choices">
        
        {/* Artists Tile */}
        <button
          onClick={() => setConfirmType("artist")}
          className="splash-choice"
          aria-label="Artists"
          data-testid="splash-stardom-btn"
        >
          <div className="splash-choice-content">
            <Mic aria-hidden="true" />
            <h3>Artists</h3>
          </div>
          <p className="text-xs sm:text-sm text-[#C0C6D0] leading-relaxed">
            Create your free artist profile today and monetise.
          </p>
        </button>

        {/* Supporters Tile */}
        <button
          onClick={() => setConfirmType("fan")}
          className="splash-choice"
          data-testid="splash-fans-btn"
        >
          <div className="splash-choice-content">
            <Crown aria-hidden="true" />
            <h3>Supporters</h3>
          </div>
          <p className="text-xs sm:text-sm text-[#C0C6D0] leading-relaxed">
            Support your favorite artist and be part of a legacy.
          </p>
        </button>

        {/* Sponsors Tile */}
        <button
          onClick={() => navigate("/sponsors")}
          className="splash-choice"
          data-testid="splash-corporate-btn"
        >
          <div className="splash-choice-content">
            <Handshake aria-hidden="true" />
            <h3>Sponsors</h3>
          </div>
          <p className="text-xs sm:text-sm text-[#C0C6D0] leading-relaxed">
            Support the movement
          </p>
        </button>

        {/* Join the Movement Tile */}
        <button
          onClick={() => navigate("/home")}
          className="splash-choice"
          aria-label="Explore"
          data-testid="splash-enter-btn"
        >
          <div className="splash-choice-content">
            <Sparkles aria-hidden="true" />
            <h3>Explore</h3>
          </div>
          <p className="text-xs sm:text-sm text-[#C0C6D0] leading-relaxed">
            Be part of the movement to make a positive change in this world.
          </p>
        </button>
      </div>

      {/* Top 100 Idols banner — irresistible CTA */}
      <div className={`mt-10 transition-all duration-1000 delay-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
        <Link to="/top-100-global-idols-2027" data-testid="splash-top100-banner" className="group block">
          <div className="px-5 sm:px-8 py-3 sm:py-4 rounded-full bg-gradient-to-r from-[#EC008C]/10 via-[#EC008C]/20 to-[#EC008C]/10 border border-[#EC008C]/40 backdrop-blur-md hover:border-[#EC008C] hover:bg-[#EC008C]/15 hover:scale-105 transition-all duration-300 flex items-center gap-2 sm:gap-3 max-w-[90vw]">
            <span className="text-xs font-bold tracking-widest text-[#EC008C] bg-[#EC008C]/20 px-2 py-0.5 rounded-full">NEW</span>
            <span className="text-white font-['Unbounded'] text-xs sm:text-sm font-semibold whitespace-nowrap overflow-hidden text-ellipsis">
              Top 100 Global Music Idols 2027
            </span>
            <span className="text-[#EC008C] text-xs sm:text-sm font-bold whitespace-nowrap">Apply Free →</span>
          </div>
        </Link>
      </div>

      {/* Tribute + Movement Statement — visible on the initial landing (inline, not overlapping) */}
      <div className={`mt-10 max-w-3xl px-6 transition-all duration-1000 delay-1000 ${loaded ? 'opacity-100' : 'opacity-0'}`} data-testid="splash-tribute">
        <div className="text-center space-y-2">
          <p className="text-[11px] sm:text-xs text-[#8A92A0] leading-relaxed">
            <span className="text-[#EC008C] font-semibold">In loving memory</span> — StardomFans.com is dedicated to <span className="text-white font-semibold">Sulli</span> and <span className="text-white font-semibold">Yu Meng Long</span>. We lost two bright stars to the industry&apos;s negative force. Let&apos;s make StardomFans.com a safe and prosperous place for all who join the Movement.
          </p>
          <p className="text-[11px] sm:text-xs text-[#8A92A0]">
            Without <span className="text-[#EC008C] font-semibold">Supporters</span> and <span className="text-[#EC008C] font-semibold">Sponsors</span>, we can not make the change. Together, we can. <span className="text-white font-semibold">Thank EVERYBODY!</span>
          </p>
        </div>
      </div>

      {/* Privacy Score — creator-first differentiator */}
      <div className={`mt-6 flex justify-center px-6 transition-all duration-1000 delay-[1200ms] ${loaded ? 'opacity-100' : 'opacity-0'}`}>
        <PrivacyScoreBadge variant="compact" />
      </div>

      {/* Confirmation Modal */}
      {confirmType && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md" data-testid="confirm-modal">
          <div className="bg-[#000000] border border-[#1A2030] rounded-2xl p-10 max-w-md w-full mx-6 text-center relative shadow-[0_0_80px_rgba(255,51,0,0.06)]">
            <button
              onClick={() => setConfirmType(null)}
              className="absolute top-5 right-5 text-[#8A92A0] hover:text-white text-xl transition-colors"
              data-testid="confirm-close-btn"
            >
              &times;
            </button>

            <img src="/logo.png" alt="" className="w-24 h-24 mx-auto mb-6 object-contain" style={{ mixBlendMode: "lighten" }} />

            {confirmType === "fan" ? (
              <>
                <h2 className="font-['Unbounded'] text-2xl font-bold text-[#E8F0FA] mb-3">Join as a Supporter</h2>
                <p className="text-[#8A92A0] text-sm leading-relaxed mb-8">
                  Discover artists, collect exclusive drops, book supporter experiences, interact with your idols, and join the global entertainment movement.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setConfirmType(null)}
                    className="flex-1 py-3.5 rounded-xl border border-[#1A2030] text-[#8A92A0] hover:text-[#E8F0FA] hover:border-[#8A92A0] transition-all font-medium"
                    data-testid="confirm-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleConfirm("fan")}
                    className="flex-1 py-3.5 rounded-xl bg-gradient-to-r from-[#EC008C] to-[#FF3399] text-black hover:shadow-[0_0_25px_rgba(255,51,0,0.4)] transition-all font-bold"
                    data-testid="confirm-fan-btn"
                  >
                    Create Supporter Account
                  </button>
                </div>
              </>
            ) : confirmType === "corporate" ? (
              <>
                <h2 className="font-['Unbounded'] text-2xl font-bold text-[#E8F0FA] mb-3">Join as a Sponsor Partner</h2>
                <p className="text-[#8A92A0] text-sm leading-relaxed mb-8">
                  Open a branded store on StardomFans.com. Sell merch and digital goods to the global supporter community. 90% revenue share. Verified sponsors only.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setConfirmType(null)}
                    className="flex-1 py-3.5 rounded-xl border border-[#1A2030] text-[#8A92A0] hover:text-[#E8F0FA] hover:border-[#8A92A0] transition-all font-medium"
                    data-testid="confirm-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleConfirm("corporate")}
                    className="flex-1 py-3.5 rounded-xl bg-gradient-to-r from-[#EC008C] to-[#FF3399] text-black hover:shadow-[0_0_25px_rgba(255,51,0,0.4)] transition-all font-bold"
                    data-testid="confirm-corporate-btn"
                  >
                    Become a Sponsor
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="font-['Unbounded'] text-2xl font-bold text-[#E8F0FA] mb-3">Join as an Artist</h2>
                <p className="text-[#8A92A0] text-sm leading-relaxed mb-8">
                  Upload music & videos, sell tickets & merch, create paid experiences, build your empire. You keep 90% of all revenue.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setConfirmType(null)}
                    className="flex-1 py-3.5 rounded-xl border border-[#1A2030] text-[#8A92A0] hover:text-[#E8F0FA] hover:border-[#8A92A0] transition-all font-medium"
                    data-testid="confirm-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleConfirm("artist")}
                    className="flex-1 py-3.5 rounded-xl bg-gradient-to-r from-[#EC008C] to-[#FF3399] text-black hover:shadow-[0_0_25px_rgba(255,51,0,0.4)] transition-all font-bold"
                    data-testid="confirm-artist-btn"
                  >
                    Create Artist Account
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SplashPage;
